import { Skeleton } from '@/components/ui-kit/skeleton';
import { Card, CardContent } from '@/components/ui-kit/card';
import { cn } from '@/lib/utils';
import { Fragment } from 'react';
import { AgentCard } from '@/modules/gpt-chats/components/agent-card';
import { useTranslation } from 'react-i18next';
import { Bot } from 'lucide-react';
import { IGetAgentsResponse } from '@/modules/gpt-chats/types/agent.service.type';
import { AddAgent } from '@/modules/gpt-chats/components/add-agent';

export const AgentsListSkeleton = ({ view }: { view: string }) => {
  return (
    <div
      className={cn(
        'grid',
        view === 'grid' && 'grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
        view === 'list' && 'gap-1.5'
      )}
    >
      {Array.from({ length: 8 }).map((_, index) => (
        <Fragment key={index}>
          {view === 'grid' ? (
            <Card className="flex flex-col items-center gap-4 rounded-lg border bg-card p-6 shadow-sm h-[240px]">
              <Skeleton className="h-24 w-24 rounded-xl" />
              <div className="flex flex-col items-center gap-2 w-full">
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            </Card>
          ) : (
            <Card className="flex h-[72px] items-center gap-4 rounded-lg border-0 bg-card p-4 shadow-none">
              <Skeleton className="h-12 w-12 rounded-lg" />
              <div className="flex flex-col gap-1 flex-1">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-4 w-48" />
              </div>
            </Card>
          )}
        </Fragment>
      ))}
    </div>
  );
};

export const AgentsList = ({
  view,
  data,
  isLoading,
}: {
  view: 'grid' | 'list';
  data: IGetAgentsResponse | undefined;
  isLoading: boolean;
}) => {
  const { t } = useTranslation();

  const agents = data?.agents || [];

  if (isLoading) {
    return <AgentsListSkeleton view={view} />;
  }

  if (agents.length === 0) {
    return (
      <Card className="flex flex-col items-center justify-center p-12 text-center border-dashed">
        <Bot className="h-12 w-12 text-muted-foreground/40 mb-4" />
        <h3 className="text-lg font-medium">{t('NO_AGENTS_AVAILABLE')}</h3>
        <p className="text-sm text-muted-foreground mt-1 mb-6">
          {t('CREATE_AGENT_TO_GET_STARTED', 'Create an agent to get started')}
        </p>
        <AddAgent />
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="pt-6">
        <div
          className={cn(
            'grid',
            view === 'grid' && 'grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
            view === 'list' && 'gap-1.5'
          )}
        >
          {agents.map((agent) => (
            <AgentCard key={agent.id} agent={agent} view={view} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
